import { Note, NoteType } from '../../../shared/models/note';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { NoteService } from '../../../shared/services/note.service';
import { IParentEntity } from 'app/shared/interfaces/entity';
import { BaseComponent } from '../base/base.component';
import { MatDialog } from '@angular/material/dialog';
import { MessageBoxComponent, MessageBoxParameters, MessageBoxType } from 'app/components/messageBox/messageBox.component';
import { AuthService } from 'app/shared/services/auth.service';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { Utilities } from 'app/shared/helpers/utilities';
import { SubjectService } from 'app/shared/services/subject.service';
import { SubjectStopwatchService } from 'app/shared/services/subject-stopwatch/subject-stopwatch.service';
import { NoteTypeService } from 'app/shared/services/note-type.service';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
    selector: 'notes',
    templateUrl: './notes.component.html',
    styleUrls: ['./notes.component.scss'],
})
export class NotesComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    hasSelectedNotes: boolean;
    hasSelectedOnlyDraftNotes: boolean;
    isIndeterminate: boolean;
    folders: any[];
    filters: any[];
    labels: any[];
    searchInput: UntypedFormControl;
    currentNote: Note;
    parent: IParentEntity;
    noteOrderAscending = false;
    userReadOnly = false;
    archived = false;

    noteTypes: NoteType[] = [];
    noteTypesList: NoteType[] = [];
    subjectTypeId: number = -1;
    showNoteTypeFilter: boolean = false;

    onSelectedNotesChanged: Subscription;
    onTypesChanged: Subscription;
    onFiltersChanged: Subscription;
    onCurrentNoteChanged: Subscription;
    onParentChanged: Subscription;
    onSearchInputChanged: Subscription;
    onSubjectItemChanged: Subscription;

    constructor(
        public dialog: MatDialog,
        private authService: AuthService,
        private subjectService: SubjectService,
        private noteService: NoteService,
        private noteTypeService: NoteTypeService,
        private subjectTypeService: SubjectTypeService,
        private subjectStopwatchService: SubjectStopwatchService,
    ) {
        super();
        this.searchInput = new UntypedFormControl('');
        this.userReadOnly = this.authService.isUserReadOnly();
    }

    ngOnInit() {
        // make sure that the initial order is in sync with the note service
        this.noteOrderAscending = this.noteService.noteOrderAscending;
        this.noteService.setCurrentNote(null);
        this.noteService.deselectNotes();        
        this.archived = (this.subjectService.currentItem && this.subjectService.currentItem.archived);

        // Ensure we get latest subject info so that we can disabled/enable AddNote button
        this.subjectService.getSubject(this.subjectService.currentItem.uid).pipe(take(1)).subscribe(e => {            
            if (e) {
                this.archived = e.archived;
            }
        });
        this.onSubjectItemChanged = this.subjectService.onItemChanged.subscribe((e) => {
            if (e) {                
                this.archived = e.archived;
            }
        });

        this.onSelectedNotesChanged =
            this.noteService.onSelectedNotesChanged
                .subscribe((selectedNotes) => {

                    setTimeout(() => {
                        this.hasSelectedNotes = selectedNotes.length > 0;
                        this.hasSelectedOnlyDraftNotes = this.hasSelectedNotes && ((selectedNotes.filter((x) => x.sequenceId).length) < 1);
                        this.isIndeterminate = (selectedNotes.length !== this.noteService.notes.length && selectedNotes.length > 0);

                        // PJT - If case is archived then do not allow any notes to be deleted, even draft notes - Work Item #8275
                        if (this.subjectService.currentItem && this.subjectService.currentItem.archived) {
                            this.hasSelectedOnlyDraftNotes = false;
                        }
                        // PJT - If case is archived then do not allow any notes to be deleted, even draft notes - Work Item #8275
                    }, 0);
                });

        this.onTypesChanged =
            this.noteService.onTypesChanged
                .subscribe((folders) => {
                    this.folders = this.noteService.types;
                });

        this.onFiltersChanged =
            this.noteService.onFiltersChanged
                .subscribe((folders) => {
                    this.filters = this.noteService.filters;
                });

        this.onCurrentNoteChanged =
            this.noteService.onCurrentNoteChanged
                .subscribe((currentNote) => {
                    if (!currentNote) {
                        this.currentNote = null;
                    }
                    else {
                        this.currentNote = currentNote;
                    }
                });

        this.onParentChanged =
            this.noteService.onParentChanged
                .subscribe((parent) => {                    
                    if ((!(this.parent && this.parent.uid)) || (parent.uid !== this.parent.uid)) {
                        this.parent = parent;
                        if (parent && parent.uid) {                        
                             // Make sure we get up to date subject data as this will enable/disable AddNotes button depending on if the case Archive Flag is set or not.
                             this.subjectService.getSubject(parent.uid).pipe(take(1)).subscribe(e => {
                                if (e) {                                                     
                                    this.archived = e.archived;
                                }
                            });
                            this.subjectTypeService.getByName(parent.subjectType).pipe(take(1)).subscribe((s) => {                                
                                if (s) {
                                    this.subjectTypeId = s.id;
                                    // this.archived = (this.subjectService.currentItem && this.subjectService.currentItem.archived);
                                }
                                setTimeout(() => { this.updateNoteTypes(); }, 200);
                            });
                        }
                    }
                });

        this.noteService.onSearchTextChanged.next('');

        this.noteTypeService.getAllData().pipe(take(1)).subscribe((d) => {
            this.noteTypesList = d.sort((a, b) => a.name.localeCompare(b.name));
        });

        this.onSearchInputChanged = this.searchInput.valueChanges.pipe(
            debounceTime(300), distinctUntilChanged())
            .subscribe((searchText) => {
                this.noteService.onSearchTextChanged.next(searchText);
            });
    }

    ngAfterViewInit(): void {
        setTimeout(() => { Utilities.patchCheckboxAria(); }, 500);
    }

    ngOnDestroy() {
        this.onSelectedNotesChanged.unsubscribe();
        this.onTypesChanged.unsubscribe();
        this.onFiltersChanged.unsubscribe();
        this.onCurrentNoteChanged.unsubscribe();
        this.onParentChanged.unsubscribe();
        this.onSearchInputChanged.unsubscribe();
        this.onSubjectItemChanged.unsubscribe();
    }

    get hasPrevious() {
        return this.noteService.hasPrevious;
    }

    get hasNext() {
        return this.noteService.hasNext;
    }

    readPrevious() {
        this.noteService.selectPrevious();
    }

    readNext() {
        this.noteService.selectNext();
    }

    setNoteOrder(noteOrderAscending: boolean) {
        if (this.noteService.noteOrderAscending !== noteOrderAscending) {
            this.noteService.toggleNotesOrder(this.parent);
        }
        this.noteOrderAscending = noteOrderAscending;
    }

    toggleSelectAll() {
        this.noteService.toggleSelectAll();
    }

    selectNotes(filterParameter?, filterValue?) {
        this.noteService.selectNotes(filterParameter, filterValue);
    }

    deselectNotes() {
        this.noteService.deselectNotes();
    }

    deSelectCurrentNote() {
        this.noteService.onCurrentNoteChanged.next(null);
    }

    toggleSensitiveOnSelectedNotes() {
        this.noteService.toggleSensitiveOnSelectedNotes().pipe(take(1)).subscribe((_) => {
            this.noteService.refreshNotes();
        });
    }

    toggleStarredOnSelectedNotes() {
        this.noteService.toggleStarredOnSelectedNotes().pipe(take(1)).subscribe((_) => {
            this.noteService.refreshNotes();
        });
    }

    toggleImportantOnSelectedNotes() {
        this.noteService.toggleImportantOnSelectedNotes().pipe(take(1)).subscribe((_) => {
            this.noteService.refreshNotes();
        });
    }

    deleteSelectedNotes() {
        const info = new MessageBoxParameters('Are you sure you want to delete the selected notes?', 'Confirm', MessageBoxType.Confirm, true);
        this.dialog.open(MessageBoxComponent, { data: info, disableClose: false, autoFocus: false }).afterClosed().subscribe((result) => {
            if (result === 'confirm') {
                this.noteService.deleteSelectedNotes(this.parent);
            }
        });
    }

    printSelectedNotes() {
        this.noteService.printSelectedNotes(this.parent);
    }

    // guard function called if the user attemps to navigate away from this route
    canDeactivate(route, state, nextState): Observable<boolean> | boolean {
        // check route - this handles any further action in relation to subject stopwatch
        // the returned value from this call will always be true as once we have handlded stopwatch
        // we don't want to prevent users from navigating away from dashboard
        return this.subjectStopwatchService.checkTime(nextState.url);
    }

    updateNoteTypes() {
        this.noteTypes = [];
        this.noteTypes.push(new NoteType({ id: -1, name: 'All', selected: true }));
        this.noteTypes.push(new NoteType({ id: 0, name: 'Blank', selected: true }));
        this.showNoteTypeFilter = false;
        if (this.noteTypesList) {
            this.noteTypesList.forEach((i) => {
                i.selected = true;
                if (!i.hideRecord) {
                    if (i.subjectTypeId === undefined || i.subjectTypeId === -1 || i.subjectTypeId === this.subjectTypeId) {
                        this.noteTypes.push(i);
                        this.showNoteTypeFilter = true;
                    }
                }
            });
        }

        this.noteService.setNoteTypeFilter(this.noteTypes.filter((i) => i.selected === true));
        this.noteService.getNotes(this.parent);
    }

    applyNoteTypeFilter(noteType: NoteType) {
        // Update selected Note Type filter
        noteType.selected = !noteType.selected;

        // If changing 'All' note type then set all note types on or off.
        if (noteType.id == -1) {
            this.noteTypes.forEach((i) => i.selected = noteType.selected);
        }
        else {
            // Check to see if all note types selected or not and update 'All' note type.
            const c1 = this.noteTypes.filter((i) => i.id !== -1);
            const c2 = c1.filter((i) => i.selected === true);
            const all = this.noteTypes.find((i) => i.id === -1);
            if (all) {
                all.selected = (c1.length === c2.length);
            }
        }

        if (this.showNoteTypeFilter === true) {
            // Update notes list
            this.noteService.setNoteTypeFilter(this.noteTypes.filter((i) => i.selected === true));
            this.noteService.getNotesByType(this.parent, 'all');
        }
    }
}
